import { useEffect, useState, useRef } from "react"
import { useNavigate, Link } from "react-router-dom"
import Loader from "../components-legacy/Loader.jsx"
import { Dialog } from "../components-legacy/Dialog.jsx"
import { api } from "../system/api.js"
import { checkoutSession } from "../system/checkoutSession.js"
import { getSymbolCurrency } from "../system/util.js"
import { STATIC_PLAN_DATA } from "../data/Plans.js"
import CheckSimpleIcon from "../assets/svg/check-simple.svg?react"

export default function ChangeSubscription() {
    const navigate = useNavigate()
    const confirmDowngradeRef = useRef(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [errorProcessing, setErrorProcessing] = useState(null)
    const [plans, setPlans] = useState(null)
    const [billingCycle, setBillingCycle] = useState('monthly')
    const [pendingDowngrade, setPendingDowngrade] = useState(null)
    const [showBillingToggle, setShowBillingToggle] = useState(false)

    useEffect(() => {
        setBillingCycle(checkoutSession.getBillingCycle() || 'monthly')

        api('subscription/change/list').then(res => {
            const products = res.products || []
            const hasMonthlyPlans = products?.some(plan => plan.billingCycle === 'monthly' && plan.slug !== 'free')
            const hasYearlyPlans = products?.some(plan => plan.billingCycle === 'yearly' && plan.slug !== 'free')
            setShowBillingToggle(hasMonthlyPlans && hasYearlyPlans)
            setBillingCycle(hasMonthlyPlans ? 'monthly' : 'yearly')
            setPlans(products)
        }).catch(err => {
            if (err.errorCode === 'CANCELATION_PENDING') {
                setError('Your subscription is pending cancellation. You cannot change plans until the cancellation is completed, but you may revert the cancellation at any time.')
            } else {
                setError('Failed to load subscription plans.')
            }
        }).finally(() => {
            setLoading(false)
        })

    }, [])

    if (loading) {
        return <Loader />
    }

    const plansFiltered = plans?.filter(plan => {
        return plan['slug'] !== 'free' && plan['billingCycle'] === billingCycle
    }).sort((a, b) => { return a.price - b.price })

    const isYearly = billingCycle === 'yearly'

    const handleBillingCycleChange = (cycle) => {
        setBillingCycle(cycle)
        checkoutSession.saveBillingCycle(cycle)
    }

    const openDowngradeConfirmation = (plan) => {
        setPendingDowngrade(plan)
        confirmDowngradeRef.current.open()
    }

    const handleDowngrade = async () => {
        if (!pendingDowngrade) return

        confirmDowngradeRef.current?.close()
        setLoading(true)
        try {
            await api('subscription/downgrade', { pid: pendingDowngrade.pid })
            navigate('/billing')
        } catch (err) {
            setErrorProcessing(err.errorCode === 'PRODUCT_CHANGE_NOT_ALLOWED'
                ? 'This plan change is not allowed due to currency or billing cycle restrictions.'
                : 'Failed to downgrade subscription.')
            setLoading(false)
        }
    }

    const handleUpgrade = (productPricingId) => {
        navigate(`/payment?type=upgrade&id=${productPricingId}`)
    }

    if (error) {
        return (
            <main className="max-w-[1328px] mx-auto px-4">
                <p className="py-2 px-4 rounded-md bg-error dark:bg-error-dark text-error-content dark:text-error-dark-content mt-4">{error}</p>
            </main>
        )
    }

    return (
        <main className="max-w-[1328px] mx-auto px-4">
            <div className="flex flex-wrap items-center justify-between my-8 gap-y-4">
                <div className="flex w-full items-center justify-between md:w-auto md:justify-start md:gap-x-6">
                    <div className="flex items-center gap-3">
                        <Link to="/billing" className="text-gray-400 hover:text-gray-700 transition-colors duration-200">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 5l-7 7 7 7" />
                            </svg>
                        </Link>
                        <h1 className="text-2xl font-semibold">Change Plan</h1>
                    </div>
                </div>
                {showBillingToggle && (
                    <div className="flex w-full md:w-auto items-center justify-center md:justify-between md:gap-8">
                        <div className="flex items-center justify-center gap-4">
                            <span
                                className={`font-medium transition-colors duration-300 ease-in-out ${!isYearly ? 'text-blue-600 font-semibold' : ''}`}>Monthly</span>

                            <label htmlFor="billingToggle" className="relative inline-block w-14 h-8 cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="billingToggle"
                                    className="hidden peer"
                                    checked={isYearly}
                                    onChange={(e) => handleBillingCycleChange(e.target.checked ? 'yearly' : 'monthly')}
                                />
                                <span className="absolute inset-0 rounded-full border-1 border-blue-600 bg-gray-200 shadow-inner transition-colors duration-300 ease-in-out"></span>
                                <span className="absolute top-1 left-1 w-6 h-6 bg-blue-600 rounded-full shadow-md transition-transform duration-300 ease-in-out peer-checked:translate-x-6"></span>
                            </label>

                            <div className="flex items-center gap-2">
                                <span className={`font-medium transition-colors duration-300 ease-in-out ${isYearly ? 'text-blue-600 font-semibold' : ''}`}>Annually &ndash; Save 20%</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {errorProcessing && <p className="error">{errorProcessing}</p>}

            <section className="@container">
                <div className="grid grid-cols-1 @xl:grid-cols-2 @4xl:grid-cols-3 @6xl:grid-cols-4 gap-6">
                    {plansFiltered && plansFiltered.map(plan => (
                        <div data-key={plan.pid} key={plan.pid} className="flex flex-col justify-start rounded-lg p-6" style={{ backgroundColor: 'var(--base-200)' }}>
                            <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                            <p className="text-sm font-medium">&quot;{STATIC_PLAN_DATA[plan.slug]?.tagline || ''}&quot;</p>
                            <hr className="border-gray-400 mt-4 mb-4" />
                            <p className="text-sm font-medium mb-2">{plan.credits === -1 ? 'Unlimited' : plan.credits.toLocaleString('en-EN')} credits
                                / {plan.creditsDays} days</p>
                            {plan.billingCycle === 'yearly' ? (
                                <>
                                    <p className="text-sm font-medium">
                                        <span
                                            className="font-semibold text-2xl">{getSymbolCurrency(plan.currency)} {(plan.price / 12).toFixed(2)}</span> per month
                                    </p>
                                    <p className="text-gray-600 text-sm mt-2">Billed at {getSymbolCurrency(plan.currency)} {plan.price} yearly</p>
                                </>
                            ) : (
                                <p className="text-sm font-medium">
                                    <span
                                        className="font-semibold text-2xl">{getSymbolCurrency(plan.currency)} {plan.price}</span> per {plan.billingCycle === 'yearly' ? 'year' : 'month'}
                                </p>
                            )}
                            <hr className="border-gray-300 mt-4 mb-4" />
                            <ul className="space-y-2 text-xs">
                                {STATIC_PLAN_DATA[plan.slug]?.list?.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-x-2">
                                        <CheckSimpleIcon width="16" height="16" className="text-blue-600 flex-shrink-0" /> <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <hr className="border-gray-300 mt-4 mb-4" />
                            <p className="text-sm font-medium mb-2">Approximate Credit Usage:</p>
                            <ul className="space-y-2 text-xs mb-4">
                                {STATIC_PLAN_DATA[plan.slug]?.credit_usage?.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-x-2">
                                        <CheckSimpleIcon width="16" height="16" className="text-blue-600 flex-shrink-0" /> <span>{item.text} <span
                                            className="font-semibold">{item.highlight}</span></span>
                                    </li>
                                ))}
                            </ul>

                            {plan.action === 'current' ? (
                                <div className="mt-auto w-full text-center py-2">
                                    <span className="text-green-600 font-semibold">Current Plan</span>
                                </div>
                            ) : plan.action === 'upgrade' ? (
                                <button
                                    className="mt-auto w-full bg-blue-600 cursor-pointer text-white rounded-sm py-2 text-base"
                                    onClick={() => handleUpgrade(plan.pid)}
                                >
                                    Upgrade
                                </button>
                            ) : plan.action === 'downgrade' ? (
                                <button
                                    className="mt-auto w-full bg-gray-400 cursor-pointer text-white rounded-sm py-2"
                                    onClick={() => openDowngradeConfirmation(plan)}
                                >
                                    Downgrade
                                </button>
                            ) : null}
                        </div>
                    ))}
                </div>
            </section>

            <Dialog ref={confirmDowngradeRef}>
                <section className="p-md lg:p-xl rounded-md min-w-[320px] bg-base-200 dark:bg-base-dark-200 text-base-content dark:text-base-dark-content transition-shadow duration-300 ease-out shadow-base dark:shadow-base-dark">
                    <div className="flex flex-col justify-center items-center">
                        <h3 className="text-center mt-4 mb-2">Confirm Downgrade</h3>
                        {pendingDowngrade && (
                            <>
                                <p className="text-center mb-2">
                                    Are you sure you want to downgrade to the <strong>{pendingDowngrade.name}</strong> plan?
                                </p>
                                <p className="text-center text-sm text-gray-600 mb-4">
                                    This will reduce your monthly credits to {pendingDowngrade.credits === -1 ? 'unlimited' : pendingDowngrade.credits.toLocaleString()}.
                                </p>
                            </>
                        )}
                        <div className="flex w-full gap-2 mt-4">
                            <button
                                className="border-1 border-blue-600 text-blue-600 w-full rounded-sm py-2 cursor-pointer"
                                onClick={() => confirmDowngradeRef.current.close()}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-orange-300 text-orange-800 w-full rounded-sm py-2 cursor-pointer"
                                onClick={() => handleDowngrade()}
                            >
                                Confirm Downgrade
                            </button>
                        </div>
                    </div>
                </section>
            </Dialog>
        </main>
    )
}
